import { useCallback, useState } from 'react';

import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { displayToast } from 'components/system/Toast';
import { axiosRestApi } from 'config/axiosRestApi';
import { useFeedbackModal } from 'providers';
import { getAxiosTooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';
import { AUTH_STEP } from '../AuthManager/constants';
import { useAuthManagerContext } from '../AuthManager/Context/useAuthManagerContext';

const DEFAULT_ERROR_PROPS: IFeedbackModal = {
  type: 'error',
  title: 'Error sending password reset email',
  message: 'Please try again later',
};

interface IResetPasswordFormValues {
  email: string;
}

const resetPasswordRequest = async (email: string) => {
  return await axiosRestApi.post('/reset-password', {
    email,
  });
};

export const useForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const { setStep, prefilledEmail } = useAuthManagerContext();
  const { openFeedbackModal } = useFeedbackModal();

  const handleBackToLogin = () => {
    setStep(AUTH_STEP.DEFAULT);
  };

  const handleFormSubmit = useCallback(
    async (fields: IResetPasswordFormValues) => {
      setLoading(true);
      try {
        await resetPasswordRequest(fields.email);
        setStep(AUTH_STEP.DEFAULT);
        displayToast(
          {
            type: 'success',
            title: 'Successful password reset',
            description: 'Please check your email to activate your account',
          },
          { autoClose: false },
        );
      } catch (error) {
        logError({ error });

        const modalProps =
          getAxiosTooManyRequestsFeedbackModalProps(error) ??
          DEFAULT_ERROR_PROPS;

        openFeedbackModal(modalProps);
      } finally {
        setLoading(false);
      }
    },
    [openFeedbackModal, setStep],
  );

  return {
    loading,
    handleFormSubmit,
    handleBackToLogin,
    prefilledEmail,
  };
};
